/* ============================================================
   NO CONTEXT CLOTHING — Theme JS  (ncc.js)
   ============================================================ */

// ---- CART — Shopify Cart API ----
window.NCC = window.NCC || {};

NCC.cart = {
  // Add variant to Shopify cart via API — optimistic UI
  async add(variantId, qty, successMsg) {
    // 1. Open drawer immediately with loading state — feels instant
    NCC.cartDrawer.setLoading(true);
    NCC.cartDrawer.open();

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: qty || 1 })
      });
      if (!res.ok) throw new Error('Add failed');
      // 2. Fetch updated cart and render with real data
      await NCC.cartDrawer.refresh();
      NCC.toast.show(successMsg || 'Added to cart ✓');
    } catch(e) {
      NCC.cartDrawer.close();
      NCC.toast.show('Could not add to cart — please try again');
    } finally {
      NCC.cartDrawer.setLoading(false);
    }
  },

  // Remove item from Shopify cart by line item key
  async remove(key) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
      await NCC.cartDrawer.refresh();
    } catch(e) {}
  },

  // Legacy shim — called from old add-to-cart buttons with (name, price, size)
  // Maps to Shopify API using the variant ID from the ATC button
  addLegacy(name, price, size) {
    const atcBtn = document.getElementById('product-atc');
    const variantId = atcBtn ? parseInt(atcBtn.dataset.variantId) : null;
    if (variantId) {
      NCC.cart.add(variantId, 1, name + ' added to cart ✓');
    } else {
      NCC.toast.show(name + ' added to cart ✓');
    }
  }
};

// ---- CART DRAWER ----
NCC.cartDrawer = {
  open() {
    document.getElementById('cart-overlay')?.classList.add('open');
    document.getElementById('cart-drawer')?.classList.add('open');
  },

  setLoading(state) {
    const container = document.getElementById('cart-items');
    const footer    = document.getElementById('cart-footer');
    if (!container) return;
    if (state) {
      container.innerHTML = `
        <div style="padding:40px 0;text-align:center;">
          <div class="cart-spinner"></div>
          <p style="color:var(--gray-mid);font-size:13px;margin-top:14px;letter-spacing:0.05em;">Adding to cart...</p>
        </div>`;
      if (footer) footer.style.display = 'none';
    }
  },

  close() {
    document.getElementById('cart-overlay')?.classList.remove('open');
    document.getElementById('cart-drawer')?.classList.remove('open');
  },

  updateBadge(count) {
    document.querySelectorAll('.cart-count').forEach(b => b.textContent = count);
  },

  async refresh() {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      this.updateBadge(cart.item_count);
      this.render(cart);
    } catch(e) {}
  },

  render(cart) {
    const container = document.getElementById('cart-items');
    const footer    = document.getElementById('cart-footer');
    const totalEl   = document.getElementById('cart-total-amount');
    if (!container) return;

    if (!cart || !cart.items || cart.items.length === 0) {
      container.innerHTML = `
        <div class="cart-drawer__empty">
          <div class="cart-drawer__empty-icon">🛒</div>
          <p>Your cart is empty.<br>Add something weird.</p>
        </div>`;
      if (footer) footer.style.display = 'none';
      return;
    }

    container.innerHTML = cart.items.map(item => {
      const imgSrc = item.featured_image?.url
        ? item.featured_image.url.replace(/(\.[a-z]+)(\?.*)?$/, '_160x160_crop_center$1')
        : null;

      // Build clean variant tags (colour + size)
      const variantParts = (item.variant_title && item.variant_title !== 'Default Title')
        ? item.variant_title.split(' / ').map(v => `<span class="cart-drawer__item-tag">${v}</span>`).join('')
        : '';

      return `
        <div class="cart-drawer__item">
          <div class="cart-drawer__item-img">
            ${imgSrc
              ? `<img src="${imgSrc}" alt="${item.product_title}" width="80" height="80" style="width:100%;height:100%;object-fit:cover;">`
              : '<span style="font-size:28px;">👕</span>'
            }
          </div>
          <div style="flex:1;min-width:0;">
            <p class="cart-drawer__item-name">${item.product_title}</p>
            ${variantParts ? `<div class="cart-drawer__item-tags">${variantParts}</div>` : ''}
            <p class="cart-drawer__item-meta">$${(item.price/100).toFixed(2)} × ${item.quantity}</p>
          </div>
          <button class="cart-drawer__item-remove" onclick="NCC.cart.remove('${item.key}')">×</button>
        </div>`;
    }).join('');

    if (footer) {
      footer.style.display = 'block';
      if (totalEl) totalEl.textContent = (cart.total_price / 100).toLocaleString('en', { style: 'currency', currency: 'USD' });
    }
  }
};

// ---- TOAST ----
NCC.toast = {
  show(msg, duration = 3000) {
    const el = document.getElementById('toast');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('show');
    clearTimeout(this._t);
    this._t = setTimeout(() => el.classList.remove('show'), duration);
  }
};

// ---- ACCORDION ----
NCC.initAccordion = function() {
  document.querySelectorAll('.accordion__btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const item = btn.closest('.accordion__item');
      const isOpen = item.classList.contains('open');
      // Close all
      document.querySelectorAll('.accordion__item.open').forEach(i => i.classList.remove('open'));
      // Open clicked if it was closed
      if (!isOpen) item.classList.add('open');
    });
  });
};

// ---- SIZE SELECTOR ----
NCC.initSizeSelector = function() {
  // Size selection handled by inline onclick="NCC.selectSize(this)" on each button
  // No additional listener needed here
};

// ---- QTY STEPPER ----
NCC.initQtyStepper = function() {
  document.querySelectorAll('.qty-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const input = btn.closest('.qty-row').querySelector('.qty-input');
      let val = parseInt(input.value) || 1;
      if (btn.dataset.dir === 'up')   val = Math.min(val + 1, 99);
      if (btn.dataset.dir === 'down') val = Math.max(val - 1, 1);
      input.value = val;
    });
  });
};

// ---- PRODUCT PAGE GALLERY ----
NCC.initGallery = function() {
  const mainImg = document.getElementById('gallery-main');
  if (!mainImg) return;

  // Preload all full-size images on page load so they're cached and instant
  document.querySelectorAll('.product-page__thumb').forEach(thumb => {
    const fullSrc = thumb.dataset.full || thumb.src;
    const preload = new Image();
    preload.src = fullSrc;
  });

  document.querySelectorAll('.product-page__thumb').forEach(thumb => {
    thumb.addEventListener('click', () => {
      const fullSrc = thumb.dataset.full || thumb.src;

      // If same image, do nothing
      if (mainImg.src === fullSrc) return;

      document.querySelectorAll('.product-page__thumb').forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');

      // Preload the new image — swap only once it's fully loaded
      const loader = new Image();
      loader.onload = () => {
        mainImg.style.transition = 'opacity 0.15s ease';
        mainImg.style.opacity = '0';
        // Tiny rAF delay ensures the fade-out renders before src swap
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            mainImg.src = fullSrc;
            mainImg.style.opacity = '1';
          });
        });
      };
      loader.src = fullSrc;
    });
  });
};

// ---- COLLECTION FILTERS ----
NCC.initFilters = function() {
  document.querySelectorAll('.filter-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const tag = btn.dataset.tag;
      const url = new URL(window.location.href);

      if (tag === 'all') {
        // Remove tag filter — go back to base collection URL
        url.pathname = url.pathname.replace(/\/[^/]+$/, function(match) {
          // Strip any existing tag segment
          return match;
        });
        // Shopify tag URLs are /collections/handle/tag-name
        // Strip tag from path if present
        const pathParts = url.pathname.split('/').filter(Boolean);
        if (pathParts[0] === 'collections' && pathParts.length === 3) {
          url.pathname = '/' + pathParts[0] + '/' + pathParts[1];
        }
      } else {
        // Shopify tag filter URL: /collections/collection-handle/tag-handle
        const pathParts = url.pathname.split('/').filter(Boolean);
        if (pathParts[0] === 'collections' && pathParts[1]) {
          url.pathname = '/collections/' + pathParts[1] + '/' + tag;
        }
      }

      // Preserve sort_by if set
      window.location.href = url.toString();
    });
  });
};

// ---- ADD TO CART (product page) ----
NCC.initProductATC = function() {
  const atcBtn = document.getElementById('product-atc');
  if (!atcBtn) return;

  atcBtn.addEventListener('click', () => {
    const selectedSize = document.querySelector('.size-btn.selected');
    if (!selectedSize && document.querySelector('.size-grid')) {
      NCC.toast.show('Please select a size first');
      document.querySelector('.size-grid')?.classList.add('size-grid--error');
      setTimeout(() => document.querySelector('.size-grid')?.classList.remove('size-grid--error'), 1200);
      return;
    }
    const variantId = parseInt(atcBtn.dataset.variantId);
    const name      = atcBtn.dataset.productName;
    const size      = selectedSize ? selectedSize.dataset.size : '';
    const qty       = parseInt(document.getElementById('qty-input')?.value) || 1;
    NCC.cart.add(variantId, qty, `${name}${size ? ' (' + size + ')' : ''} added to cart ✓`);
  });
};

// ---- SHOPIFY CHECKOUT ----
NCC.proceedToCheckout = function() {
  window.location.href = '/checkout';
};


/* ============================================================
   FAQ CATEGORY FILTER
   ============================================================ */
NCC.faq = {
  filter(category, btn) {
    // Update active button
    document.querySelectorAll('.faq-nav__btn').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');

    // Show/hide FAQ items
    document.querySelectorAll('.faq-item').forEach(item => {
      if (category === 'all' || item.dataset.category === category) {
        item.style.display = 'block';
      } else {
        item.style.display = 'none';
        item.classList.remove('open');
      }
    });
  }
};

// ---- INIT ----
document.addEventListener('DOMContentLoaded', () => {
  // Load real cart from Shopify on page load
  NCC.cartDrawer.refresh();
  NCC.initAccordion();
  NCC.initSizeSelector();
  NCC.initQtyStepper();
  NCC.initGallery();

  // Preload all colour variant images so swaps are instant
  document.querySelectorAll('.product-colour-swatch[data-image]').forEach(btn => {
    if (btn.dataset.image) { const pre = new Image(); pre.src = btn.dataset.image; }
  });
  NCC.initFilters();
  NCC.initProductATC();

  // Cart open/close bindings
  document.getElementById('cart-btn')?.addEventListener('click', NCC.cartDrawer.open.bind(NCC.cartDrawer));
  document.getElementById('cart-overlay')?.addEventListener('click', NCC.cartDrawer.close.bind(NCC.cartDrawer));
  document.getElementById('cart-close')?.addEventListener('click', NCC.cartDrawer.close.bind(NCC.cartDrawer));
  // cart-checkout-btn is a plain <a href="/checkout"> — no JS needed
});

/* ============================================================
   SEARCH DRAWER
   ============================================================ */
NCC.search = {
  isOpen: false,
  debounceTimer: null,
  lastQuery: '',

  open() {
    const drawer  = document.getElementById('search-drawer');
    const overlay = document.getElementById('search-overlay');
    const input   = document.getElementById('search-drawer-input');
    if (!drawer) return;
    drawer.classList.add('open');
    overlay?.classList.add('open');
    document.body.style.overflow = 'hidden';
    this.isOpen = true;
    // Focus input after transition
    setTimeout(() => input?.focus(), 60);
  },

  close() {
    const drawer  = document.getElementById('search-drawer');
    const overlay = document.getElementById('search-overlay');
    if (!drawer) return;
    drawer.classList.remove('open');
    overlay?.classList.remove('open');
    document.body.style.overflow = '';
    this.isOpen = false;
    this.hideLive();
  },

  setQuery(term) {
    const input = document.getElementById('search-drawer-input');
    if (input) {
      input.value = term;
      input.dispatchEvent(new Event('input'));
      input.focus();
    }
  },

  showLive() {
    document.getElementById('drawer-trending')?.setAttribute('hidden', '');
    document.getElementById('drawer-live')?.removeAttribute('hidden');
    document.getElementById('drawer-no-results')?.setAttribute('hidden', '');
  },

  showTrending() {
    document.getElementById('drawer-trending')?.removeAttribute('hidden');
    document.getElementById('drawer-live')?.setAttribute('hidden', '');
    document.getElementById('drawer-no-results')?.setAttribute('hidden', '');
  },

  showNoResults(term) {
    document.getElementById('drawer-trending')?.setAttribute('hidden', '');
    document.getElementById('drawer-live')?.setAttribute('hidden', '');
    const noRes = document.getElementById('drawer-no-results');
    noRes?.removeAttribute('hidden');
    const termEl = document.getElementById('no-results-term');
    if (termEl) termEl.textContent = `"${term}"`;
  },

  hideLive() {
    this.showTrending();
  },

  async fetchAndRender(query) {
    if (query.length < 2) { this.showTrending(); return; }
    if (query === this.lastQuery) return;
    this.lastQuery = query;

    try {
      const url  = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][fields]=title,product_type,variants.title,tag`;
      const res  = await fetch(url);
      const data = await res.json();
      const products = data.resources?.results?.products || [];

      if (!products.length) {
        this.showNoResults(query);
        return;
      }

      const liveEl = document.getElementById('drawer-live');
      if (!liveEl) return;

      const highlight = (text) => {
        const re = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        return text.replace(re, '<mark>$1</mark>');
      };

      liveEl.innerHTML = `
        <div class="search-drawer__live-header">
          <p class="search-drawer__section-label">PRODUCTS</p>
          <a href="/search?q=${encodeURIComponent(query)}&type=product" class="search-drawer__live-see-all">
            See all ${products.length > 5 ? '6+' : products.length} results →
          </a>
        </div>
        ${products.map(p => {
          const price  = p.price ? `$${(p.price / 100).toFixed(2)}` : '';
          const imgSrc = p.featured_image?.url
            ? p.featured_image.url.replace(/(\.[a-z]+)(\?.*)?$/, '_160x160_crop_center$1')
            : null;
          return `
            <a href="${p.url}" class="predictive-item" role="option" tabindex="0">
              <div class="predictive-item__img">
                ${imgSrc
                  ? `<img src="${imgSrc}" alt="${p.title}" width="56" height="56" loading="lazy">`
                  : '<span>👕</span>'
                }
              </div>
              <div class="predictive-item__info">
                <span class="predictive-item__name">${highlight(p.title)}</span>
                ${p.product_type ? `<span class="predictive-item__type">${p.product_type}</span>` : ''}
              </div>
              <span class="predictive-item__price">${price}</span>
            </a>`;
        }).join('')}
      `;
      this.showLive();

    } catch(e) {
      this.showTrending();
    }
  },

  init() {
    const input   = document.getElementById('search-drawer-input');
    const closeBtn = document.getElementById('search-drawer-close');
    const overlay  = document.getElementById('search-overlay');
    const navSearchBtn = document.querySelector('[aria-label="Search"]');

    // Open from nav
    navSearchBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.open();
    });

    // Close
    closeBtn?.addEventListener('click', () => this.close());
    overlay?.addEventListener('click', () => this.close());

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    // Input — debounced predictive fetch
    input?.addEventListener('input', (e) => {
      const q = e.target.value.trim();
      clearTimeout(this.debounceTimer);
      if (q.length < 2) { this.showTrending(); this.lastQuery = ''; return; }
      this.debounceTimer = setTimeout(() => this.fetchAndRender(q), 220);
    });

    // Arrow key nav in results
    input?.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const first = document.querySelector('#drawer-live .predictive-item');
        first?.focus();
      }
    });

    document.getElementById('drawer-live')?.addEventListener('keydown', (e) => {
      const items = [...document.querySelectorAll('#drawer-live .predictive-item')];
      const idx   = items.indexOf(document.activeElement);
      if (e.key === 'ArrowDown' && idx < items.length - 1) {
        e.preventDefault(); items[idx + 1].focus();
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        idx > 0 ? items[idx - 1].focus() : input?.focus();
      }
    });
  }
};

// Wire up search on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  NCC.search.init();
});

/* ============================================================
   MOBILE MENU
   ============================================================ */
NCC.mobileMenu = {
  isOpen: false,

  open() {
    const menu = document.getElementById('mobile-menu');
    const btn  = document.getElementById('hamburger-btn');
    if (!menu) return;
    menu.classList.add('open');
    menu.setAttribute('aria-hidden', 'false');
    btn?.classList.add('open');
    btn?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    this.isOpen = true;
  },

  close() {
    const menu = document.getElementById('mobile-menu');
    const btn  = document.getElementById('hamburger-btn');
    if (!menu) return;
    menu.classList.remove('open');
    menu.setAttribute('aria-hidden', 'true');
    btn?.classList.remove('open');
    btn?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    this.isOpen = false;
  },

  toggle() {
    this.isOpen ? this.close() : this.open();
  },

  init() {
    const btn = document.getElementById('hamburger-btn');
    btn?.addEventListener('click', () => this.toggle());

    // Close on nav link click
    document.querySelectorAll('.mobile-menu__links a').forEach(a => {
      a.addEventListener('click', () => this.close());
    });

    // Close on resize back to desktop
    window.addEventListener('resize', () => {
      if (window.innerWidth > 860 && this.isOpen) this.close();
    });
  }
};

document.addEventListener('DOMContentLoaded', () => {
  NCC.mobileMenu.init();
});

/* ---- Fix mobile menu top offset to sit below sticky header ---- */
document.addEventListener('DOMContentLoaded', () => {
  function setMobileMenuOffset() {
    const header = document.querySelector('.site-header');
    const menu   = document.getElementById('mobile-menu');
    if (header && menu) {
      menu.style.top = header.offsetHeight + 'px';
    }
  }
  setMobileMenuOffset();
  window.addEventListener('resize', setMobileMenuOffset);
});

/* ---- Set header height CSS var for sticky toolbar offset ---- */
document.addEventListener('DOMContentLoaded', () => {
  function setHeaderHeight() {
    const header = document.querySelector('.site-header');
    if (header) {
      document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
    }
  }
  setHeaderHeight();
  window.addEventListener('resize', setHeaderHeight);
});

/* ============================================================
   AJAX SORT — swaps product grid without page reload
   ============================================================ */
NCC.sortAjax = async function(url) {
  const grid    = document.getElementById('collection-grid');
  const select  = document.querySelector('.sort-select');
  if (!grid) { window.location.href = url; return; }

  // Save scroll position
  const scrollY = window.scrollY;

  // Visual loading state
  grid.style.opacity = '0.4';
  grid.style.pointerEvents = 'none';
  grid.style.transition = 'opacity 0.2s';
  if (select) select.disabled = true;

  try {
    // Fetch the sorted page as raw HTML
    const res  = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    const html = await res.text();

    // Parse the response and extract just the grid
    const parser  = new DOMParser();
    const doc     = parser.parseFromString(html, 'text/html');
    const newGrid = doc.getElementById('collection-grid');

    if (newGrid) {
      // Swap grid content
      grid.innerHTML = newGrid.innerHTML;

      // Update the URL without reloading (browser history)
      window.history.pushState({}, '', url);

      // Update selected option in the dropdown
      if (select) {
        Array.from(select.options).forEach(opt => {
          opt.selected = opt.value === url;
        });
      }

      // Restore scroll position — user stays exactly where they were
      window.scrollTo({ top: scrollY, behavior: 'instant' });

    } else {
      // Fallback: full page load if grid not found in response
      window.location.href = url;
    }

  } catch(e) {
    // Fallback on any error
    window.location.href = url;
  } finally {
    grid.style.opacity = '1';
    grid.style.pointerEvents = 'auto';
    if (select) select.disabled = false;
  }
};
